import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { ResultReviewContext } from "@/components/result_review/primitives";
import { ResultPdfPane } from "@/components/result_review/ResultPdfPane";
import { ResultHeader } from "@/components/result_review/ResultHeader";
import { ReviewAlerts } from "@/components/result_review/ReviewAlerts";
import { AssemblySummary } from "@/components/result_review/AssemblySummary";
import { ComponentCard } from "@/components/result_review/ComponentCard";
import { UnassignedAnnotations } from "@/components/result_review/DrawingAnnotations";
import { EvidenceIndex, SecondarySections } from "@/components/result_review/SecondarySections";
import { ReconciliationPanel } from "@/components/result_review/ReconciliationPanel";
import { ReconciliationApplyPanel } from "@/components/result_review/ReconciliationApplyPanel";
import { HistoricalDrawingContents } from "@/components/result_review/HistoricalDrawingContents";
import { OperationalReviewAction } from "@/components/result_review/OperationalReviewAction";

/** P6O — Revisione risultato IA: PDF a sinistra, risultato fuso persistito a destra (vista predefinita). Solo GET. */
export default function HardenedResultReview() {
  const { jobId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedV = searchParams.get("v_opening_mm") || "";
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [recon, setRecon] = useState(null);
  const [reconErr, setReconErr] = useState(null);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setD(null); setErr(null); setRecon(null); setReconErr(null);
    api.get(`/reader/jobs/${jobId}/hardened-result`, { signal: controller.signal, params: selectedV ? { v_opening_mm: selectedV } : {} })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setD(data);
        if (!["VERIFIED_HISTORICAL_SECTION", "ARCHIVED_SINGLE_PART"].includes(data.result_kind)) {
          api.get(`/reader/jobs/${jobId}/hardened-result/reconciliation`, { signal: controller.signal })
            .then(({ data: r }) => setRecon(r))
            .catch(e => { if (!controller.signal.aborted) setReconErr(formatApiErrorDetail(e.response?.data?.detail)); });
        }
      })
      .catch(e => { if (!controller.signal.aborted) setErr(formatApiErrorDetail(e.response?.data?.detail, { status: e.response?.status, network: !e.response })); });
    return () => controller.abort();
  }, [jobId, selectedV]);

  const select = useCallback((segId, evId) => {
    setSelected({ segId, evId });
    const ev = (d?.evidence_by_segment?.[segId] || []).find((x) => x.evidence_id === evId);
    if (ev?.page) setPage(ev.page);
  }, [d]);

  const ctx = useMemo(() => ({ evidenceBySegment: d?.evidence_by_segment || {}, selected, select, page }), [d, selected, select, page]);

  const exportJson = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/reader/jobs/${jobId}/hardened-result/export.json`, { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `${d.run.run_id}_merged_result.json`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Esportato ${name}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  if (err) return <div className="p-8 text-xs" data-testid="result-review-error"><div className="border border-red-300 bg-red-50 p-3 text-red-900">{err}</div></div>;
  if (!d) return <div className="p-8 text-xs text-muted-foreground" data-testid="result-review-loading">Lettura del risultato persistito…</div>;

  if (["VERIFIED_HISTORICAL_SECTION", "ARCHIVED_SINGLE_PART"].includes(d.result_kind)) return <ResultReviewContext.Provider value={ctx}>
    <HistoricalDrawingContents d={d} jobId={jobId} page={page} setPage={setPage} pageCount={pageCount} setPageCount={setPageCount}
      selectedV={selectedV} onSelect={v => setSearchParams(v ? { v_opening_mm: v } : {})} />
  </ResultReviewContext.Provider>;

  const bomByCode = Object.fromEntries(d.merged.package.bom_rows.map((r) => [r.code, r]));
  const provByCode = Object.fromEntries(d.components_provenance.map((p) => [p.component_code, p]));
  const reconByCode = Object.fromEntries((recon?.projection?.components || []).map((c) => [c.component_code, c]));

  return (
    <ResultReviewContext.Provider value={ctx}>
      <div className="p-3 sm:p-4 space-y-2 text-xs" data-testid="result-review-page">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate("/reader-queue")} data-testid="result-back-btn"><ArrowLeft size={12} className="mr-1" />Coda letture</Button>
          {!d.job.operational_upload && <span className="text-[10px] text-muted-foreground">Archivio · risultato conservato</span>}
        </div>
        {d.job.operational_upload ? <header className="space-y-2 py-3" data-testid="operational-result-header"><h1 className="text-2xl font-display font-bold break-words" data-testid="operational-result-title">{d.job.filename}</h1><p className="text-sm" data-testid="operational-result-status">Dati e lavorazioni da verificare · costo lettura confermato ${Number(d.cost.confirmed_total_usd).toFixed(4)} USD</p></header> : <ResultHeader d={d} onExport={exportJson} exporting={exporting} />}
        {d.job.operational_upload && !d.active_revision?.has_revision && <OperationalReviewAction jobId={jobId} onPrepared={async () => { const r = await api.get(`/reader/jobs/${jobId}/hardened-result`); setD(r.data); }} />}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-2 items-start lg:items-stretch lg:h-[calc(100vh-13rem)]">
          <div className="h-[60vh] lg:h-full flex flex-col min-h-0">
            <ResultPdfPane jobId={jobId} page={page} setPage={setPage} pageCount={pageCount} setPageCount={setPageCount} missingPages={d.review_items.missing_pages} />
          </div>
          <div className="space-y-2 min-w-0 lg:h-full lg:overflow-y-auto lg:pr-1" data-testid="result-right-pane">
            <ReviewAlerts d={d} />
            {!d.job.operational_upload && (recon ? <ReconciliationPanel r={recon} /> : (
              <div className="border-2 border-indigo-300 bg-indigo-50 p-2 text-[11px]" data-testid="recon-unavailable">
                {reconErr ? (d.active_revision?.has_revision
                  ? <span data-testid="recon-already-applied-note">Proposta di riconciliazione non ricalcolata: revisione deterministica già applicata su questo documento (stato e dettagli nel pannello «Applicazione revisione»).</span>
                  : <>Riconciliazione bloccata fail-closed: {reconErr}</>) : "Calcolo della proiezione di riconciliazione…"}
              </div>
            ))}
            {!d.job.operational_upload && <ReconciliationApplyPanel jobId={jobId} />}
            <AssemblySummary d={d} />
            <div className="space-y-2" data-testid="result-components">
              {d.merged.components.map((c) => (
                <ComponentCard key={c.component_code} c={c} prov={provByCode[c.component_code]} bomRow={bomByCode[c.component_code]}
                  notes={d.notes_by_segment[provByCode[c.component_code]?.source_segment_id]} recon={reconByCode[c.component_code]} />
              ))}
            </div>
            <UnassignedAnnotations d={d} />
            <details data-testid="result-technical-details"><summary className="cursor-pointer font-semibold py-2" data-testid="result-technical-toggle">Tracciabilità e dettagli tecnici</summary>{d.job.operational_upload && <ResultHeader d={d} onExport={exportJson} exporting={exporting} />}<EvidenceIndex d={d} /><SecondarySections d={d} />{d.job.operational_upload && recon && <ReconciliationPanel r={recon} />}</details>
          </div>
        </div>
        {(d.active_revision?.has_revision || d.job.operational_upload) && <Link className="inline-flex border bg-white px-5 py-3 rounded-sm font-semibold" data-testid="open-quote-workspace" to={`/quote-preparation/${jobId}`}>Rivedi il pezzo →</Link>}
      </div>
    </ResultReviewContext.Provider>
  );
}
